/*
 * Product Family section
 * ----------------------
 * Extends the theme's Global.Core base class and uses its section-scoped
 * pub/sub bus (this.sectionId / this.sub). DOM access is kept explicit
 * (querySelector) so the component is robust across theme updates.
 *
 * Elements defined here:
 *   <product-family>          orchestrator: model switch (Section Rendering
 *                             fetch of ?view=<panel-view>), scroll-spy sub-nav,
 *                             sticky-bar visibility.
 *   <product-family-gallery>  numeric image counter (subscribes to the theme's
 *                             gallery slide-change event).
 *   <product-family-toggle>   "Learn more" description expander.
 *   <product-family-sticky>   sticky add-to-cart expand/collapse panel.
 */
const { Core, Events } = Global;

const REGIONS = ['gallery', 'pricehead', 'buybox', 'sticky', 'sticky-cta', 'features', 'specs', 'reviews'];

customElements.define('product-family', class extends Core {
  render() {
    this._panelCache = {};          // productUrl -> parsed Document
    this._activeIndex = this._initialActiveIndex();
    this._switching = false;

    this._models = Array.from(this.querySelectorAll('[data-element="model-option"]'));
    this._select = this.querySelector('[data-element="model-select"]');
    this._cards = Array.from(this.querySelectorAll('[data-element="compare-card"]'));

    this._onModelListClick = this._onModelListClick.bind(this);
    this._onSelectChange = this._onSelectChange.bind(this);
    this._onCompareClick = this._onCompareClick.bind(this);
    this._onAnchorClick = this._onAnchorClick.bind(this);

    const modelList = this.querySelector('[data-element="model-list"]');
    if (modelList) modelList.addEventListener('click', this._onModelListClick);
    if (this._select) this._select.addEventListener('change', this._onSelectChange);
    // Comparison-card "Select" buttons sync with the model selector.
    this.addEventListener('click', this._onCompareClick);

    const anchorList = this.querySelector('[data-element="anchor-list"]');
    if (anchorList) anchorList.addEventListener('click', this._onAnchorClick);

    // Defined BEFORE _initStickyVisibility: its first evaluation already needs
    // to know whether the always-on mobile sticky mode applies.
    this._stickyMQ = window.matchMedia('(max-width: 991px)');

    this._initScrollSpy();
    this._initStickyVisibility();
    this._initVariantDelegation();
    this._initSeeMoreSync();
    this._initCompareSwipeGate();
    this._initGalleryAxisLock();
    this._initExpandPinch();
    this._syncModelTabindex();
    this._initModelKeys();
    this._prefetchPanels();

    // Bring the selected model's card into view once the compare slider has
    // initialised (it always starts at slide 0; the active model may be a
    // later card, especially on mobile's one-card view).
    this._whenCompareSliderReady(() => this._showCardInSlider(this._activeIndex));

    // Equalize the compare cards' row zones after first paint (fonts/layout
    // settled), and again whenever the viewport changes wrap points.
    requestAnimationFrame(() => this._equalizeCardRows());
    window.addEventListener('load', () => this._equalizeCardRows(), { once: true });
    this._equalizeResizeHandler = () => {
      clearTimeout(this._equalizeTimer);
      this._equalizeTimer = setTimeout(() => {
        this._tagMultilineOptions();
        this._equalizeCardRows();
        this._syncStickySpace();
        this._fitModelSelect();
      }, 200);
    };
    window.addEventListener('resize', this._equalizeResizeHandler, { passive: true });

    // Shrink overlong model titles so the selected one fits the sub-nav
    // dropdown. Re-measured once fonts have actually loaded.
    requestAnimationFrame(() => this._fitModelSelect());
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this._fitModelSelect());
    }

    // Mobile sticky Add to cart: re-evaluate the bar (and the space reserved for
    // it at the bottom of the page) whenever the viewport crosses the breakpoint.
    this._onStickyMQ = () => {
      this._syncStickySpace();
      if (this._evaluateSticky) this._evaluateSticky();
    };
    if (this._stickyMQ.addEventListener) this._stickyMQ.addEventListener('change', this._onStickyMQ);
    // Both, explicitly: with the drawer switched off there is no drawer to
    // populate, but the always-on bar still needs its height reserved.
    requestAnimationFrame(() => {
      this._syncStickyDrawer();
      this._syncStickySpace();
    });

    // The variant options are a grid item of the model list; `order` slots them
    // under the selected model. No Step 1 / Step 2 headings any more.
    this._syncOptionsPlacement();

    // Step 2 option buttons: a wrapped label turns the full-pill button into
    // an egg — tag wrapped buttons @multiline so the CSS switches those to
    // rounded rectangles. The buybox subtree is replaced on model switches
    // and by the provider on variant changes, so retag on any subtree change
    // (class toggles don't fire childList mutations, so this can't loop).
    requestAnimationFrame(() => this._tagMultilineOptions());
    this._multilineObserver = new MutationObserver(() => {
      clearTimeout(this._multilineTimer);
      this._multilineTimer = setTimeout(() => this._tagMultilineOptions(), 60);
    });
    this._multilineObserver.observe(this, { childList: true, subtree: true });

    // The theme's sticky header has no CSS var exposing its height, and a one-time
    // measurement is wrong whenever other bars scroll away or the header hides —
    // so track its ACTUAL bottom edge and pin the sub-nav directly beneath it.
    const themeHeader = document.querySelector('.\\#header');
    if (themeHeader) {
      let ticking = false;
      const updateOffset = () => {
        const bottom = Math.max(0, Math.round(themeHeader.getBoundingClientRect().bottom));
        this.style.setProperty('--pf-subnav-top', bottom + 'px');
        ticking = false;
      };
      updateOffset();
      this._headerOffsetHandler = () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(updateOffset);
        }
      };
      window.addEventListener('scroll', this._headerOffsetHandler, { passive: true });
      window.addEventListener('resize', this._headerOffsetHandler, { passive: true });
    }
  }

  // Variant-change reactions, via native DOM event delegation ('change' bubbles).
  // NOTE: do NOT use pub/sub here — this theme's pub/sub delivers strictly within a
  // matching section-id scope (verified live; the { global: true } option does not
  // cross scopes), and the buybox publishes under the 'product-family-panel' scope
  // while this element carries the collection section's id.
  //  1. Regions: re-fetch + swap gallery (curated image set), specs (Components
  //     group), price head and the sticky bar for the selected variant.
  //  2. Variant id: set the hidden name="id" inputs immediately. The theme's provider
  //     re-renders them after its (async) section fetch; setting them synchronously
  //     closes the race where a fast Add-to-cart submits the previous variant.
  //  3. Variant gaps: when the clicked combination has no variant, the PF
  //     provider (allow-unavailable-variants) re-renders the buybox into the
  //     stock disabled-"Unavailable" state; follow it via the listeners'
  //     'update' events and mirror it onto the sticky bar (details below).
  _initVariantDelegation() {
    // Seed the gap-follower (listener 3 below) with the initially rendered
    // variant, so provider cache republishes on load don't read as changes.
    this._lastVariantSync = this._currentFormVariantId();

    // CAPTURE phase, deliberately: when the provider already has the target variant
    // cached (e.g. toggling back to the default), it re-renders the selector
    // synchronously in microtasks DURING event dispatch — by the bubble phase the
    // clicked radio is detached and its ancestry checks fail. Capture runs first.
    this.addEventListener('change', (e) => {
      const input = e.target;
      if (!input || !input.matches || !input.matches('input[type="radio"]')) return;
      if (!input.closest('[data-pf-region="buybox"]') && !input.closest('[data-pf-options]')) return;

      const variantId = input.dataset.variantId;

      if (variantId) {
        // Re-render the variant-dependent regions (gallery: curated image set;
        // specs: the Components group) for the selected variant.
        clearTimeout(this._gallerySyncTimer);
        this._gallerySyncTimer = setTimeout(() => this._syncVariantRegions(variantId), 150);
      }

      if (variantId) {
        this._lastVariantSync = String(variantId);
        this.querySelectorAll('[data-pf-region="buybox"] input[name="id"], [data-pf-options] input[name="id"]').forEach((idInput) => {
          idInput.value = variantId;
        });
        this._updateCardVariantSpecs(variantId);
        this._updatePriceSpans(variantId);
      }
    }, true);

    // 3. Variant GAPS — option combinations with no variant (e.g. Fathom
    //    Standard Complete: Strata / 105mm Dad Bod 80a does not exist). The
    //    stock radio snippet renders no data-variant-id on those values, so
    //    the handler above has nothing to act on. Merchant-decided UX
    //    (2026-09-02): gaps are ENTERABLE and SIGNALED, never carted — the PF
    //    provider renders allow-unavailable-variants (see
    //    snippets/product-family-panel.liquid), so a gap click re-renders the
    //    buybox into the theme's stock unavailable state: checked pills fade,
    //    the buy-box button goes disabled "Unavailable", and the form's
    //    hidden id EMPTIES. Every neighbouring pill stays clickable, so any
    //    next click recovers.
    //    Those re-renders land as listener updates, NOT 'change' events, so
    //    follow them here: every product-variant-listener dispatches a
    //    bubbling 'update' CustomEvent after re-rendering. Read the settled
    //    id off the form's hidden input at event time (never off the event —
    //    a later click may already have superseded it):
    //    - EMPTY id → mirror the unavailable state onto the sticky bar. Its
    //      CTA is not a provider listener; left alone it would stay an active
    //      Add to cart submitting an empty id — the live "Parameter Missing
    //      or Invalid: items" failure this replaces.
    //    - REAL id → sync the PF regions (also covers a provider-side
    //      correction if smart select is ever re-enabled for PF).
    //    _lastVariantSync makes the ordinary direct-variantId path (already
    //    synced above) a no-op here.
    this.addEventListener('update', () => {
      const variantId = this._currentFormVariantId();
      if (!variantId) {
        // Re-run on EVERY update while unavailable (no dedupe): the provider
        // re-renders its listeners one by one, and the buy-box button this
        // mirrors from only exists once the ATC listener's own update has
        // landed — the first update of the batch is too early. Idempotent.
        this._lastVariantSync = variantId;
        this._setStickyUnavailable();
        return;
      }
      if (variantId === this._lastVariantSync) return;
      this._lastVariantSync = variantId;
      clearTimeout(this._gallerySyncTimer);
      this._gallerySyncTimer = setTimeout(() => this._syncVariantRegions(variantId), 150);
      this._updateCardVariantSpecs(variantId);
      this._updatePriceSpans(variantId);
    });
  }

  // Mirror the buy box's unavailable state onto the sticky bar's CTA (used by
  // both the desktop scroll-triggered bar and the mobile pinned bar — same
  // nodes). The sticky regions are normally swapped by _syncVariantRegions,
  // which needs a REAL variant id to fetch, so the no-variant state has to be
  // applied client-side: disable the button and copy the buy box's own
  // server-rendered label ("Unavailable", localized) over its content.
  // Temporary by construction — the next valid selection swaps the whole
  // sticky-cta region in fresh from the server.
  _setStickyUnavailable() {
    const cta = this.querySelector('[data-pf-region="sticky-cta"] [data-element="to-cart-button"]');
    if (!cta) return;
    cta.disabled = true;
    const buyboxBtn = this.querySelector('[data-pf-region="buybox"] button[disabled]');
    const label = buyboxBtn && buyboxBtn.textContent.trim();
    if (label) cta.textContent = label;
  }

  // The authoritative selected-variant id: the ACTIVE model's form-associated
  // hidden input, wherever the travelling options node currently sits (in the
  // buybox card or moved into the sticky drawer). Empty string when the
  // selection has no variant (gap state, smart selector disabled).
  _currentFormVariantId() {
    const idInput = this.querySelector('[data-pf-region="buybox"] input[name="id"], [data-pf-options] input[name="id"]');
    return (idInput && idInput.value) || '';
  }

  // The Step 1 model button and the comparison card pre-render one price
  // block per variant ([data-pf-price-variant]); show the selected variant's
  // and hide the rest, so both displays follow the buy-box selection. Only
  // the ACTIVE model's button/card are touched — other models keep showing
  // their own default variant's price.
  _updatePriceSpans(variantId) {
    const scopes = [];
    const model = this._models[this._activeIndex];
    if (model) scopes.push(model);
    const card = this._cards.find((c) => Number(c.dataset.pfIndex) === this._activeIndex);
    if (card) scopes.push(card);
    let toggled = false;
    scopes.forEach((scope) => {
      const spans = scope.querySelectorAll('[data-pf-price-variant]');
      if (!spans.length) return;
      let matched = false;
      spans.forEach((el) => {
        const hit = el.dataset.pfPriceVariant === String(variantId);
        if (hit) matched = true;
      });
      // Unknown variant id: leave the current display alone rather than blank it.
      if (!matched) return;
      spans.forEach((el) => {
        el.hidden = el.dataset.pfPriceVariant !== String(variantId);
      });
      toggled = true;
    });
    // Price blocks can differ in height (sale badges) — re-level the card rows.
    if (toggled) requestAnimationFrame(() => this._equalizeCardRows());
  }

  // The CSS min-heights on the card zones assume desktop wrap points; on
  // narrow screens long values (trucks/wheels names, installment copy) wrap to
  // different line counts per card and the rows drift out of alignment. This
  // measures each zone across ALL cards (custom-complete cards included) and
  // pins every card's zone to the tallest, so rows line up while swiping.
  // Compare each Step 2 option label's rendered height to its line height;
  // more than one line means the pill shape can't work — tag it for the CSS.
  _tagMultilineOptions() {
    this.querySelectorAll('.\\#product-options-radio-item-body').forEach((body) => {
      const title = body.querySelector('.\\#product-options-radio-item-title') || body;
      const lineHeight = parseFloat(getComputedStyle(title).lineHeight) || 24;
      const lines = Math.round(title.getBoundingClientRect().height / lineHeight);
      body.classList.toggle('@multiline', lines > 1);
    });
  }

  _equalizeCardRows() {
    const cards = Array.from(this.querySelectorAll('.\\#product-family-card'));
    if (cards.length < 2) return;
    const zones = [
      '.\\#product-family-card-title-row',
      '.\\#product-family-card-price',
      '[data-pf-vspec="trucks"]',
      '[data-pf-vspec="wheels"]',
      '[data-pf-vspec="bearings"]',
      '[data-pf-vspec="bushings"]',
    ];
    zones.forEach((sel) => {
      const els = cards
        .map((c) => c.querySelector(sel))
        .filter((el) => el && !el.hidden);
      if (els.length < 2) return;
      let max = 0;
      els.forEach((el) => {
        el.style.minHeight = '';
        const h = el.getBoundingClientRect().height;
        if (h > max) max = h;
      });
      els.forEach((el) => {
        el.style.minHeight = Math.ceil(max) + 'px';
      });
    });
  }

  // The compare slider (slideshow-inline-blaze) initialises asynchronously —
  // poll briefly for its Blaze instance before positioning it.
  _whenCompareSliderReady(cb, tries = 0) {
    const el = this.querySelector('.\\#product-family-compare slideshow-inline-blaze');
    if (el && el.slider) return cb();
    if (tries > 60) return;
    requestAnimationFrame(() => this._whenCompareSliderReady(cb, tries + 1));
  }

  // Slide the Model Options row so the card for the given model index is in
  // view. Uses Blaze's own states (each state's page = [firstSlide, lastSlide]
  // currently visible) so it works for any slides-per-view, including the
  // fractional mobile peek. No-op when the card is already visible.
  _showCardInSlider(index) {
    const el = this.querySelector('.\\#product-family-compare slideshow-inline-blaze');
    const slider = el && el.slider;
    if (!slider || !slider.states || slider.isStatic) return;
    const slides = Array.from(slider.slides);
    const target = slides.findIndex((s) => {
      const card = s.matches('[data-element="compare-card"]') ? s : s.querySelector('[data-element="compare-card"]');
      return card && Number(card.dataset.pfIndex) === index;
    });
    if (target < 0) return;
    let stateIdx = slider.states.findIndex((st) => st.page[0] <= target && target <= st.page[1]);
    if (stateIdx < 0) stateIdx = Math.min(target, slider.states.length - 1);
    const diff = stateIdx - slider.stateIndex;
    if (diff > 0) slider.next(diff);
    else if (diff < 0) slider.prev(-diff);
  }

  // Blaze translates the track on EVERY touchmove by the horizontal delta and
  // commits a slide change at just 10px — so a vertical page scroll with
  // natural finger wobble wiggles the tiles and can flip slides. Gate the
  // slider's touchmove stream (capture phase, so we run before the track's own
  // listeners): swallow moves until ~12px of travel decides the gesture, then
  // keep swallowing if it is vertical-ish. Only clearly horizontal gestures
  // reach Blaze. Listeners are passive — native pan-y scrolling is untouched.
  _initCompareSwipeGate() {
    const zone = this.querySelector('.\\#product-family-compare');
    if (!zone) return;
    let startX = 0;
    let startY = 0;
    let decided = false;
    let blockDrag = false;
    zone.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      decided = false;
      blockDrag = false;
    }, { capture: true, passive: true });
    zone.addEventListener('touchmove', (e) => {
      if (!decided) {
        const dx = Math.abs(e.touches[0].clientX - startX);
        const dy = Math.abs(e.touches[0].clientY - startY);
        if (dx + dy < 12) {
          e.stopPropagation();
          return;
        }
        decided = true;
        // Horizontal only when clearly so (dx > ~1.4x dy); ties go to scrolling.
        blockDrag = dy >= dx * 0.7;
      }
      if (blockDrag) e.stopPropagation();
    }, { capture: true, passive: true });
  }

  // The gallery slider wobbles under a vertical page scroll for the same
  // reason the compare row did (see _initCompareSwipeGate): Blaze ignores the
  // Y axis entirely — every touchmove translates the track by the horizontal
  // delta, and ±10px of drift at finger-lift flips the slide. And in the other
  // direction, a deliberate horizontal swipe still lets the browser pan the
  // page vertically, so both axes move at once. This gate makes gallery
  // touches single-axis: the first ~12px of travel decides the gesture, then
  // the losing axis is locked out for the rest of that touch.
  //
  // Delegated from the host in the CAPTURE phase because the gallery region —
  // slider included — is replaced on every model/variant swap; a listener on
  // the slider itself would die on the first switch. Scoped to the INLINE
  // slider only (the expand modal's zoom/pan is _initExpandPinch's, and a
  // second finger hands the touch off to it untouched). touchmove is
  // passive:false — the horizontal lock needs preventDefault to stop native
  // page scrolling, guarded by e.cancelable so a scroll the browser already
  // owns degrades to today's behavior instead of throwing.
  _initGalleryAxisLock() {
    const st = { active: false, decided: false, horizontal: false, x: 0, y: 0 };
    this._onGalleryTouchStart = (e) => {
      st.active = false;
      if (e.touches.length !== 1) return;
      const zone = e.target.closest && e.target.closest('[data-pf-region="gallery"] product-gallery-slider');
      if (!zone || !this.contains(zone)) return;
      st.active = true;
      st.decided = false;
      st.horizontal = false;
      st.x = e.touches[0].clientX;
      st.y = e.touches[0].clientY;
    };
    this._onGalleryTouchMove = (e) => {
      if (!st.active) return;
      if (e.touches.length !== 1) { st.active = false; return; }
      if (!st.decided) {
        const dx = Math.abs(e.touches[0].clientX - st.x);
        const dy = Math.abs(e.touches[0].clientY - st.y);
        if (dx + dy < 12) {
          // Undecided: Blaze sees nothing (no track wiggle), the page may scroll.
          e.stopPropagation();
          return;
        }
        st.decided = true;
        // Same tie-break as the compare gate: horizontal only when clearly so
        // (dx > ~1.4x dy); ties go to scrolling.
        st.horizontal = dy < dx * 0.7;
      }
      if (st.horizontal) {
        // Strict left-right: the browser never starts a vertical page scroll.
        if (e.cancelable) e.preventDefault();
      } else {
        // Strict up-down: Blaze never sees the gesture, the track never moves.
        e.stopPropagation();
      }
    };
    this._onGalleryTouchEnd = () => { st.active = false; };
    this.addEventListener('touchstart', this._onGalleryTouchStart, { capture: true, passive: true });
    this.addEventListener('touchmove', this._onGalleryTouchMove, { capture: true, passive: false });
    this.addEventListener('touchend', this._onGalleryTouchEnd, { capture: true, passive: true });
    this.addEventListener('touchcancel', this._onGalleryTouchEnd, { capture: true, passive: true });
  }

  /* ---------------- Model list: roving tabindex (radiogroup) ---------------- */

  // The model list is ONE tab stop, not one per model. Without this every model
  // button is separately tabbable, and because the options panel is positioned
  // by CSS `order` (DOM order != visual order) focus jumps BACKWARDS past it:
  // selected model -> the models below the panel -> back up to the panel.
  // A single tab stop collapses the order to
  //   selected model -> options -> Add to cart
  // which is exactly the visual order. WCAG 2.4.3.
  //
  // It also makes the existing role="radiogroup" / role="radio" markup honest:
  // those roles have always promised arrow-key navigation that was never
  // implemented, so screen readers announced "radio group, 1 of 3" while the
  // arrow keys did nothing.
  _syncModelTabindex(index) {
    const active = typeof index === 'number' ? index : this._activeIndex;
    this._models.forEach((btn) => {
      btn.tabIndex = Number(btn.dataset.pfIndex) === active ? 0 : -1;
    });
  }

  _initModelKeys() {
    const list = this.querySelector('[data-element="model-list"]');
    if (!list) return;
    const KEYS = ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft', 'Home', 'End'];
    this._onModelKeydown = (e) => {
      if (!KEYS.includes(e.key)) return;
      const btn = e.target.closest && e.target.closest('[data-element="model-option"]');
      if (!btn) return;
      // Work in ARRAY position, not data-pf-index: a Product block with no
      // product selected leaves a gap in the index sequence.
      const pos = this._models.indexOf(btn);
      if (pos < 0) return;
      const last = this._models.length - 1;
      let next;
      if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = last;
      else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') next = pos === last ? 0 : pos + 1;
      else next = pos === 0 ? last : pos - 1;
      const target = this._models[next];
      if (!target || target === btn) return;
      e.preventDefault(); // arrows would otherwise scroll the page
      target.focus();
      // Arrows SELECT, per the radiogroup pattern. Safe despite the switch being
      // expensive: _selectModel returns early while _switching, and _fetchPanel
      // caches per (product, variant), so arrowing back and forth costs nothing.
      this._selectModel(Number(target.dataset.pfIndex), target.dataset.pfUrl, target);
    };
    list.addEventListener('keydown', this._onModelKeydown);
  }

  /* ---------------- Pinch-to-zoom in the expanded gallery ---------------- */

  // The stock <product-gallery-expand-zoom> deliberately binds NOTHING on
  // touch: its render() is `MediaQueries.MOBILE.matches || (...)`, which
  // short-circuits the whole initialisation at <=991px. Its zoom is a
  // mouse-oriented contain<->cover toggle panned with mousemove.
  //
  // On product pages that gap is masked by the Variant Image Wizard app, which
  // ships its own Swiper gallery — but VIW only runs on product templates, so
  // on a Family collection page the expanded image is simply inert. This adds
  // real pinch/pan/double-tap, delegated from the host so it survives every
  // gallery region swap, and scoped to this element so PDP galleries (VIW's or
  // the theme's) are untouched.
  _initExpandPinch() {
    if (!window.matchMedia('(pointer: coarse)').matches && !('ontouchstart' in window)) return;

    const MAX = 4;
    const st = this._pinch = {
      el: null, img: null, scale: 1, x: 0, y: 0,
      startDist: 0, startScale: 1, startX: 0, startY: 0, panX: 0, panY: 0,
      lastTap: 0, pinching: false,
    };

    const zoomEl = (e) => {
      const el = e.target && e.target.closest && e.target.closest('product-gallery-expand-zoom');
      return el && this.contains(el) ? el : null;
    };
    // offsetWidth/Height are LAYOUT sizes — unaffected by the transform we are
    // about to write, unlike getBoundingClientRect().
    const clamp = () => {
      if (!st.img) return;
      const maxX = Math.max(0, (st.img.offsetWidth * st.scale - st.img.offsetWidth) / 2);
      const maxY = Math.max(0, (st.img.offsetHeight * st.scale - st.img.offsetHeight) / 2);
      st.x = Math.min(maxX, Math.max(-maxX, st.x));
      st.y = Math.min(maxY, Math.max(-maxY, st.y));
    };
    // translate BEFORE scale: the pan offset then stays in unscaled screen px.
    const apply = () => {
      if (st.img) st.img.style.transform = `translate(${st.x}px, ${st.y}px) scale(${st.scale})`;
    };
    const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);

    this._resetPinch = () => {
      this.querySelectorAll('product-gallery-expand-zoom').forEach((z) => {
        const img = z.querySelector('.\\#product-gallery-expand-zoom-img');
        if (img) img.style.transform = '';
        z.classList.remove('!zoomed', '!pinching');
      });
      st.el = null; st.img = null; st.scale = 1; st.x = 0; st.y = 0; st.pinching = false;
    };

    this._onPinchStart = (e) => {
      const z = zoomEl(e);
      if (!z) return;
      if (st.el && st.el !== z) this._resetPinch();
      st.el = z;
      st.img = z.querySelector('.\\#product-gallery-expand-zoom-img');
      if (!st.img) { st.el = null; return; }

      if (e.touches.length === 2) {
        st.pinching = true;
        st.startDist = dist(e.touches);
        st.startScale = st.scale;
        z.classList.add('!pinching');
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (e.touches.length === 1) {
        const now = Date.now();
        if (now - st.lastTap < 300) {
          st.scale = st.scale > 1 ? 1 : 2.5;
          st.x = 0; st.y = 0;
          clamp(); apply();
          z.classList.toggle('!zoomed', st.scale > 1);
          st.lastTap = 0;
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        st.lastTap = now;
        st.startX = e.touches[0].clientX;
        st.startY = e.touches[0].clientY;
        st.panX = st.x; st.panY = st.y;
      }
    };

    this._onPinchMove = (e) => {
      if (!st.el || !st.img) return;
      if (st.pinching && e.touches.length === 2) {
        const d = dist(e.touches);
        if (st.startDist > 0) {
          st.scale = Math.min(MAX, Math.max(1, st.startScale * (d / st.startDist)));
          clamp(); apply();
          st.el.classList.toggle('!zoomed', st.scale > 1);
        }
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (e.touches.length === 1 && st.scale > 1) {
        st.x = st.panX + (e.touches[0].clientX - st.startX);
        st.y = st.panY + (e.touches[0].clientY - st.startY);
        clamp(); apply();
        // Capture phase + stopPropagation: keeps the Blaze track from swiping
        // the slide out from under a zoomed image.
        e.preventDefault();
        e.stopPropagation();
      }
    };

    this._onPinchEnd = () => {
      if (!st.el) return;
      st.pinching = false;
      st.el.classList.remove('!pinching');
      if (st.scale <= 1.01) {
        st.scale = 1; st.x = 0; st.y = 0;
        apply();
        st.el.classList.remove('!zoomed');
      }
    };
    this._onPinchModalClose = () => this._resetPinch();

    this.addEventListener('touchstart', this._onPinchStart, { capture: true, passive: false });
    this.addEventListener('touchmove', this._onPinchMove, { capture: true, passive: false });
    this.addEventListener('touchend', this._onPinchEnd, { capture: true, passive: true });
    this.addEventListener('modal-close', this._onPinchModalClose);
  }

  // One "See more" toggles EVERY card's details together — the cards are
  // equal-height, so a single card expanding stretches all borders anyway.
  // 'toggle' does not bubble; a capture listener on this ancestor still sees it.
  // Assigning .open the value it already has fires no event, so the fan-out
  // converges without loops.
  _initSeeMoreSync() {
    this.addEventListener('toggle', (e) => {
      const details = e.target;
      if (!details.matches || !details.matches('.\\#product-family-card-more')) return;
      this.querySelectorAll('.\\#product-family-card-more').forEach((d) => {
        if (d !== details) d.open = details.open;
      });
    }, true);
  }

  // Re-render the variant-dependent regions (gallery + specs + sticky bar) for
  // the selected variant. Last request wins: a stale response is dropped on
  // rapid toggling.
  async _syncVariantRegions(variantId) {
    const model = this._models[this._activeIndex];
    const url = model && model.dataset.pfUrl;
    if (!url) return;
    const token = (this._gallerySyncToken = (this._gallerySyncToken || 0) + 1);
    const doc = await this._fetchPanel(url, variantId);
    if (!doc || token !== this._gallerySyncToken) return;
    this._swapRegion('gallery', doc);
    this._swapRegion('specs', doc);
    // The price head and sticky bar sit OUTSIDE the buybox provider's subtree,
    // so the provider never re-renders them on variant change — swap the whole
    // regions (the fetched doc is rendered with ?variant=). The options block
    // is NOT swapped here: it is a provider listener and re-renders itself.
    this._swapRegion('pricehead', doc);
    this._swapRegion('sticky', doc);
    this._swapRegion('sticky-cta', doc);
    if (this._evaluateSticky) this._evaluateSticky();
  }

  // Sync the ACTIVE comparison card's build-spec rows (Trucks / Wheels / Bearings,
  // variant metafields complete.*) to the selected variant, using the JSON map the
  // card embeds server-side. Rows with no value for the variant are hidden.
  _updateCardVariantSpecs(variantId) {
    const card = this._cards.find((c) => Number(c.dataset.pfIndex) === this._activeIndex);
    if (!card) return;
    const mapEl = card.querySelector('[data-pf-vspecs-map]');
    if (!mapEl) return;
    let map;
    try {
      map = JSON.parse(mapEl.textContent);
    } catch (err) {
      return;
    }
    const specs = map[variantId];
    if (!specs) return;
    card.querySelectorAll('[data-pf-vspec]').forEach((row) => {
      const value = specs[row.dataset.pfVspec];
      const valEl = row.querySelector('[data-pf-vspec-value]');
      if (value) {
        if (valEl) valEl.textContent = value;
        row.hidden = false;
      } else {
        row.hidden = true;
      }
    });
    // Card image follows the selected variant's main image.
    if (specs.image) {
      const img = card.querySelector('[data-pf-card-image] img');
      if (img) {
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.src = specs.image;
      }
      // The Step 1 model button's thumbnail follows it too (same image map).
      const modelBtn = this._models.find((b) => Number(b.dataset.pfIndex) === this._activeIndex);
      const btnImg = modelBtn && modelBtn.querySelector('[data-pf-model-thumb] img');
      if (btnImg) {
        btnImg.removeAttribute('srcset');
        btnImg.removeAttribute('sizes');
        btnImg.src = specs.image;
      }
    }
    // New values can change wrap counts — re-level the rows.
    this._equalizeCardRows();
  }

  destroy() {
    if (this._spyObserver) this._spyObserver.disconnect();
    if (this._stickyObserver) this._stickyObserver.disconnect();
    if (this._multilineObserver) this._multilineObserver.disconnect();
    if (this._stickyMQ && this._stickyMQ.removeEventListener) this._stickyMQ.removeEventListener('change', this._onStickyMQ);
    // Bottom spacing is set on <body>, outside this element — release it, or
    // the editor leaves dead padding behind on every section reload.
    document.body.classList.remove('pf-sticky-space');
    document.documentElement.style.removeProperty('--pf-sticky-space');
    if (this._equalizeResizeHandler) window.removeEventListener('resize', this._equalizeResizeHandler);
    if (this._onModelKeydown) {
      const list = this.querySelector('[data-element="model-list"]');
      if (list) list.removeEventListener('keydown', this._onModelKeydown);
    }
    if (this._onPinchStart) {
      this.removeEventListener('touchstart', this._onPinchStart, { capture: true });
      this.removeEventListener('touchmove', this._onPinchMove, { capture: true });
      this.removeEventListener('touchend', this._onPinchEnd, { capture: true });
      this.removeEventListener('modal-close', this._onPinchModalClose);
    }
    if (this._onGalleryTouchStart) {
      this.removeEventListener('touchstart', this._onGalleryTouchStart, { capture: true });
      this.removeEventListener('touchmove', this._onGalleryTouchMove, { capture: true });
      this.removeEventListener('touchend', this._onGalleryTouchEnd, { capture: true });
      this.removeEventListener('touchcancel', this._onGalleryTouchEnd, { capture: true });
    }
    if (this._headerOffsetHandler) {
      window.removeEventListener('scroll', this._headerOffsetHandler);
      window.removeEventListener('resize', this._headerOffsetHandler);
    }
  }

  _initialActiveIndex() {
    const active = this.querySelector('[data-element="model-option"].\\@active');
    if (!active) return 0;
    return Number(active.dataset.pfIndex) || 0;
  }

  /* ---------------- Model switching ---------------- */

  _onModelListClick(e) {
    const btn = e.target.closest('[data-element="model-option"]');
    if (!btn) return;
    this._selectModel(Number(btn.dataset.pfIndex), btn.dataset.pfUrl, btn);
  }

  _onSelectChange(e) {
    const opt = e.target.selectedOptions[0];
    if (!opt) return;
    this._selectModel(Number(e.target.value), opt.dataset.pfUrl);
  }

  _onCompareClick(e) {
    // Card image clicks (compare-media) select the model too — same path as the
    // Select button; the detail page is reached via the card's Learn More link.
    // No scroll: the shopper stays where they are (the card's own active state
    // is the feedback).
    const btn = e.target.closest('[data-element="compare-select"], [data-element="compare-media"]');
    if (btn) {
      const anchor = btn.closest('[data-element="compare-card"]') || btn;
      this._selectModel(Number(btn.dataset.pfIndex), btn.dataset.pfUrl, anchor);
      return;
    }
    // The ENTIRE card is a select target — clicking the title, price, or spec
    // rows picks that model. Interactive children keep their own behavior:
    // the Learn More link navigates, the "See more" accordion toggles, and
    // buttons are handled above. (Build cards carry no compare-card attribute.)
    const card = e.target.closest('[data-element="compare-card"]');
    if (!card) return;
    if (e.target.closest('a, details, summary, button')) return;
    const sel = card.querySelector('[data-element="compare-select"]');
    if (!sel) return;
    this._selectModel(Number(card.dataset.pfIndex), sel.dataset.pfUrl, card);
  }

  // anchorEl: keep this element at the same viewport position across the swap.
  // The regions above the click point change height when they re-render (other
  // model = other gallery/options), which would otherwise yank the page.
  async _selectModel(index, url, anchorEl) {
    if (index === this._activeIndex || this._switching) return;
    if (!url) return;
    this._switching = true;
    // Visual in-flight state: everything variant-reactive on screen still
    // belongs to the OUTGOING model until the fetched panel swaps in. CSS under
    // [pf-switching] dims and locks those regions so stale options are never
    // presented as current (they used to sit fully interactive for the whole
    // fetch+preload window - very visible on slow connections).
    this.setAttribute('pf-switching', '');
    this._setActiveModel(index);
    // Commit the index BEFORE the region swaps: swapping the buybox re-runs
    // _syncOptionsPlacement(), which would otherwise slot the options under the
    // previously selected model. Re-entry is already blocked by _switching.
    this._activeIndex = index;

    const doc = await this._fetchPanel(url);
    if (doc) {
      // Warm the incoming panel's images first: swapping with undecoded images
      // makes the gallery re-settle hundreds of px AFTER the swap, yanking the
      // page under the shopper. Time-boxed so a slow image can't stall the UI.
      await this._preloadPanelImages(doc);
      const anchorTop = anchorEl ? anchorEl.getBoundingClientRect().top : null;
      // The shopper's drawer stays open across a model switch. The drawer itself
      // is STATIC section markup (a sibling of the swappable regions), so it
      // survives the swap untouched — but the options node sitting inside it
      // belongs to the OUTGOING model, and the incoming buybox carries its own.
      // Drop the stale one here so exactly one [data-pf-options] exists when
      // _swapRegion('buybox') re-runs _syncOptionsPlacement(), which then hands
      // the new options straight back to the still-open drawer.
      //
      // Collapsing the bar was the previous way to get the node out of the
      // drawer before the swap. It worked, but it closed a drawer the shopper
      // had deliberately opened and bounced the options back into the card.
      // Removing the node achieves the same invariant without touching state
      // the shopper owns.
      const staleOpts = this.querySelector('[data-element="sticky-specs"] [data-pf-options]');
      if (staleOpts) staleOpts.remove();
      REGIONS.forEach((region) => this._swapRegion(region, doc));
      this._afterSwap();
      if (anchorEl && anchorTop !== null) {
        // The swapped regions keep settling for a few frames after the swap
        // (images decode, the buybox provider re-renders), so a one-shot
        // correction misses most of the shift. Instead, pin the anchor at its
        // original viewport offset for a short window, frame by frame.
        // 'instant', explicitly — the theme sets scroll-behavior: smooth on
        // <html>, which would turn each correction into a visible glide.
        // A user wheel/touch cancels the hold so we never fight their scroll.
        const holdUntil = Date.now() + 2500;
        let holdCancelled = false;
        const cancelHold = () => { holdCancelled = true; };
        window.addEventListener('wheel', cancelHold, { once: true, passive: true });
        window.addEventListener('touchstart', cancelHold, { once: true, passive: true });
        const hold = () => {
          if (holdCancelled) return;
          const delta = anchorEl.getBoundingClientRect().top - anchorTop;
          if (Math.abs(delta) > 0.5) window.scrollBy({ top: delta, left: 0, behavior: 'instant' });
          if (Date.now() < holdUntil) requestAnimationFrame(hold);
        };
        requestAnimationFrame(hold);
      }
    }
    this._switching = false;
    this.removeAttribute('pf-switching');
  }

  _setActiveModel(index) {
    this._models.forEach((btn) => {
      const on = Number(btn.dataset.pfIndex) === index;
      btn.classList.toggle('@active', on);
      btn.setAttribute('aria-checked', on ? 'true' : 'false');
    });
    this._cards.forEach((card) => {
      const on = Number(card.dataset.pfIndex) === index;
      card.classList.toggle('@active', on);
      const sel = card.querySelector('[data-element="compare-select"]');
      if (sel) sel.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    if (this._select && Number(this._select.value) !== index) {
      this._select.value = String(index);
    }
    // The closed control now shows a different title — refit its font.
    this._fitModelSelect();
    // Slot the variant options under the newly selected model. _activeIndex is
    // only assigned after the swap completes, so pass the index explicitly.
    this._syncOptionsPlacement(index);
    this._syncModelTabindex(index);
    // "Build a Custom Complete" buttons are rendered per model — show only the
    // active model's. (The sticky bar's secondary needs no bookkeeping here:
    // it renders server-side inside the swapped sticky-cta region.)
    this.querySelectorAll('[data-pf-cc]').forEach((cc) => {
      cc.hidden = Number(cc.dataset.pfIndex) !== index;
    });
    // Keep the Model Options row showing the selected model.
    this._showCardInSlider(index);
  }

  // Shrink overlong titles in the sub-nav model dropdown. A native <select>
  // clips silently, so per-option we compute the font-size that fits and set
  // it inline (honored inside the open popup everywhere except the OS-native
  // pickers on macOS/iOS), and the control itself gets the SELECTED option's
  // size — only a too-long title shrinks; short ones render at full size.
  // Measure-only: reads the select's box, never resizes it.
  _fitModelSelect() {
    const select = this._select;
    if (!select || !select.options.length) return;
    // Clear our own inline size first or it feeds back into baseSize.
    select.style.fontSize = '';
    const style = getComputedStyle(select);
    const baseSize = parseFloat(style.fontSize);
    // Inner text space: content box minus padding and the native arrow.
    const avail = select.clientWidth
      - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight) - 30;
    if (!baseSize || avail <= 0) return;
    if (!this._fitCanvas) this._fitCanvas = document.createElement('canvas');
    const ctx = this._fitCanvas.getContext('2d');
    ctx.font = `${style.fontStyle} ${style.fontWeight} ${baseSize}px ${style.fontFamily}`;
    const MIN_SCALE = 0.7;
    Array.from(select.options).forEach((opt) => {
      const width = ctx.measureText(opt.text.trim()).width;
      if (width > avail) {
        const scale = Math.max(avail / width, MIN_SCALE);
        opt.style.fontSize = (baseSize * scale).toFixed(2) + 'px';
      } else {
        opt.style.fontSize = '';
      }
    });
    const selected = select.options[select.selectedIndex];
    select.style.fontSize = (selected && selected.style.fontSize) || '';
  }

  // Load the fetched panel's region images into the browser cache (with the
  // same srcset/sizes, so the exact candidates the DOM will pick get warmed).
  _preloadPanelImages(doc) {
    const jobs = [];
    const seen = new Set();
    doc.querySelectorAll('[data-pf-region] img').forEach((img) => {
      const key = (img.getAttribute('srcset') || '') + '|' + (img.getAttribute('src') || '');
      if (!img.getAttribute('src') || seen.has(key)) return;
      seen.add(key);
      jobs.push(new Promise((resolve) => {
        const im = new Image();
        im.onload = resolve;
        im.onerror = resolve;
        if (img.getAttribute('sizes')) im.sizes = img.getAttribute('sizes');
        if (img.getAttribute('srcset')) im.srcset = img.getAttribute('srcset');
        im.src = img.getAttribute('src');
      }));
    });
    if (!jobs.length) return Promise.resolve();
    return Promise.race([
      Promise.all(jobs),
      new Promise((resolve) => setTimeout(resolve, 1500)),
    ]);
  }

  // Warm the panel cache (and its images) for the non-active models during
  // idle time after window load, so a model switch usually needs no network
  // round-trip. Without this, the first click on each model waits a full
  // fetch + image preload while the outgoing model's options sit on screen -
  // the visible "wrong options for a second" lag on slow connections.
  // Sequential (one panel at a time), lowest priority, skipped for shoppers on
  // Save-Data or 2G, and inert if the element left the DOM (section reload).
  _prefetchPanels() {
    if (this.hasAttribute('no-cache')) return;
    const conn = navigator.connection;
    if (conn && (conn.saveData || /(^|[^3-9])2g/.test(conn.effectiveType || ''))) return;
    const urls = this._models
      .map((btn) => btn.dataset.pfUrl)
      .filter((u, i, a) => u && a.indexOf(u) === i);
    // Non-active models first (most likely next click); the active model's
    // panel LAST - its content is already inline, but returning to it after
    // switching away needs the fetched panel, so warm it too.
    const active = this._models.find((btn) => Number(btn.dataset.pfIndex) === this._activeIndex);
    const activeUrl = active && active.dataset.pfUrl;
    const queue = urls.filter((u) => u !== activeUrl);
    if (activeUrl) queue.push(activeUrl);
    if (!queue.length) return;
    const start = () => {
      const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 2500));
      idle(async () => {
        for (const url of queue) {
          if (!this.isConnected) return;
          const doc = await this._fetchPanel(url);
          if (doc && this.isConnected) await this._preloadPanelImages(doc);
        }
      });
    };
    if (document.readyState === 'complete') start();
    else window.addEventListener('load', start, { once: true });
  }

  // Fetch a product's panel HTML via the family-panel alternate template.
  // Pass variantId to render the panel with that variant selected (drives the
  // variant's curated gallery set). Cached per (product, variant).
  async _fetchPanel(url, variantId) {
    const base = url.split('?')[0];
    const key = variantId ? `${base}::${variantId}` : base;
    if (this._panelCache[key]) return this._panelCache[key];
    const view = this.getAttribute('panel-view') || 'family-panel';
    const noCache = this.hasAttribute('no-cache');
    let fetchUrl = `${base}?view=${encodeURIComponent(view)}`;
    if (variantId) fetchUrl += `&variant=${encodeURIComponent(variantId)}`;
    try {
      const res = await fetch(fetchUrl, { headers: { 'X-Requested-With': 'fetch' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      if (!noCache) this._panelCache[key] = doc;
      return doc;
    } catch (err) {
      console.error('[product-family] panel fetch failed:', err);
      return null;
    }
  }

  _swapRegion(region, doc) {
    const src = doc.querySelector(`[data-pf-region="${region}"]`);
    const dest = this.querySelector(`[data-pf-region="${region}"]`);
    if (!src || !dest) return;
    // Incoming content already carries the stable "product-family-panel" scope
    // (buybox provider/listeners) and the panel section id (gallery), matching the
    // inline render — so we do NOT rewrite section-id here.
    const nodes = Array.from(src.childNodes).map((n) => document.importNode(n, true));
    dest.replaceChildren(...nodes);
    // Keep the region's own data-* state (media count / curated flag) in sync too.
    Array.from(src.attributes).forEach((attr) => {
      if (attr.name.indexOf('data-pf-') === 0) dest.setAttribute(attr.name, attr.value);
    });
    // A buybox swap replaces the provider, and with it the options node — put
    // the fresh one back under the selected model.
    if (region === 'buybox') {
      this._syncOptionsPlacement();
    }
    // A freshly-swapped head row always arrives with a collapsed chevron; the
    // drawer (section-owned) keeps whatever state it had, so re-sync the two.
    // 'sticky-cta' matters too: it carries the metafield-driven secondary
    // button, whose presence feeds the empty-drawer check.
    if (region === 'sticky' || region === 'sticky-cta') {
      const bar = this.querySelector('product-family-sticky');
      if (bar && typeof bar.syncToggle === 'function') bar.syncToggle();
      // The chevron lives INSIDE the 'sticky' region, so a swap replaces it —
      // re-apply the empty-drawer state to the new button, or a hidden chevron
      // comes back on every model switch.
      this._syncStickyDrawer();
    }
  }

  // The mobile drawer's content IS the live options block (moved in by
  // _syncOptionsPlacement), so there is nothing to render here — only the
  // question of whether the chevron has anything to reveal. An empty drawer
  // should not advertise itself: hide the chevron when the model has no options
  // AND no secondary button. The secondary renders server-side inside the
  // swapped sticky-cta region, so its presence is read from there.
  _syncStickyDrawer() {
    const opts = this.querySelector('[data-pf-options]');
    const secondary = this.querySelector('[data-pf-region="sticky-cta"] .\\#product-family-sticky-secondary');
    const empty = !opts && !secondary;
    this.querySelectorAll('[data-element="sticky-toggle"]').forEach((t) => { t.hidden = empty; });
    // Guarded on @open: collapse() re-enters this method via the bar's
    // _setOpen -> _syncOptionsPlacement chain, and would otherwise recurse.
    if (empty) {
      const bar = this.querySelector('product-family-sticky');
      if (bar && bar.classList.contains('@open') && typeof bar.collapse === 'function') bar.collapse();
    }
    this._syncStickySpace();
  }

  // Where the variant options live. Normally they stay put inside the provider
  // and CSS `order` positions them under the selected model — the node must not
  // leave the provider, whose re-render map is built by querying its own
  // subtree in the fetched document.
  //
  // The one exception is the open mobile drawer, which genuinely needs the node
  // moved into it. That is safe because it is user-initiated long after the
  // provider has cached, and _selectModel collapses the drawer (returning the
  // node) before any region swap can replace the provider underneath it.
  _syncOptionsPlacement(index) {
    const active = typeof index === 'number' ? index : this._activeIndex;
    const opts = this.querySelector('[data-pf-options]');
    if (!opts) {
      this.toggleAttribute('options-attached', false);
      this._syncStickyDrawer();
      return;
    }

    const bar = this.querySelector('product-family-sticky');
    const drawer = this.querySelector('[data-element="sticky-specs"]');
    const open = !!bar && bar.classList.contains('@open');

    if (open && drawer) {
      if (opts.parentElement !== drawer) drawer.appendChild(opts);
      opts.style.order = '';
    } else {
      const meta = this.querySelector('[data-pf-region="buybox"] [data-element="product-meta"]');
      if (meta && opts.parentElement !== meta) {
        meta.insertBefore(opts, meta.querySelector('.\\#product-family-buybox-atc'));
      }
      // Model buttons carry order = index * 2, so odd slots sit between them.
      opts.style.order = String(active * 2 + 1);
    }
    // 'options-attached' means the panel is VISUALLY attached under the
    // selected model button — placement, not existence. While the open mobile
    // drawer borrows the node the button must render as a standalone selected
    // button (closed ring, rounded corners), not with an open bottom edge.
    this.toggleAttribute('options-attached', !(open && drawer));
    this._syncStickyDrawer();
  }

  // An always-on fixed bar (mobile sticky mode) covers the bottom of the page,
  // footer included — reserve its height on <body>. Measured from the COLLAPSED
  // bar: the drawer is transient, and reserving its height would leave a gap
  // under the page whenever it is closed.
  _syncStickySpace() {
    const bar = this.querySelector('product-family-sticky');
    const on = this.hasAttribute('sticky-mobile') && this._stickyMQ && this._stickyMQ.matches && bar;
    if (!on) {
      document.body.classList.remove('pf-sticky-space');
      document.documentElement.style.removeProperty('--pf-sticky-space');
      return;
    }
    if (!bar.classList.contains('@open')) {
      const h = Math.round(bar.getBoundingClientRect().height);
      if (h > 0) document.documentElement.style.setProperty('--pf-sticky-space', h + 'px');
    }
    document.body.classList.add('pf-sticky-space');
  }

  _afterSwap() {
    // The buybox now belongs to another model/product — re-seed the gap
    // follower with the incoming variant id, so the fresh provider's
    // initialization republish doesn't read as a smart-select correction
    // (which would redundantly re-swap regions that were just swapped).
    this._lastVariantSync = this._currentFormVariantId();
    // The scroll-spy observer watches the stable overview wrapper (only the
    // inner regions swap), so it stays attached. The sticky bar node IS
    // replaced, so re-evaluate its visibility against the current scroll.
    if (this._evaluateSticky) this._evaluateSticky();
    this._refreshYotpo();
  }

  // The reviews region now holds a fresh Yotpo widget for the newly-selected
  // product; ask Yotpo to (re-)initialise it. Guarded so a missing/loading
  // Yotpo never throws.
  _refreshYotpo() {
    try {
      const y = window.yotpo;
      if (y && typeof y.refreshWidgets === 'function') y.refreshWidgets();
    } catch (err) {
      /* no-op */
    }
  }

  /* ---------------- Sub-nav: smooth scroll + scroll-spy ---------------- */

  _onAnchorClick(e) {
    const link = e.target.closest('a[href^="#"]');
    if (!link) return;
    const target = document.getElementById(link.getAttribute('href').slice(1));
    if (!target) return;
    e.preventDefault();
    // Set the clicked anchor active immediately and hold the scroll-spy while the
    // smooth scroll settles — otherwise short sections (e.g. Specs) let the spy's
    // mid-viewport band land on the NEXT section and steal the active state.
    if (link.dataset.pfTarget) {
      this.querySelectorAll('[data-element="anchor"]').forEach((a) => {
        a.classList.toggle('!active', a === link);
      });
      this._spyHoldUntil = Date.now() + 1200;
    }
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  _initScrollSpy() {
    const anchors = Array.from(this.querySelectorAll('[data-element="anchor"]'));
    if (!anchors.length) return;
    const sections = anchors
      .map((a) => this.querySelector(`[data-pf-anchor="${a.dataset.pfTarget}"]`))
      .filter(Boolean);
    if (!sections.length) return;

    this._spyObserver = new IntersectionObserver(
      (entries) => {
        if (Date.now() < (this._spyHoldUntil || 0)) return;
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const name = entry.target.getAttribute('data-pf-anchor');
          anchors.forEach((a) => a.classList.toggle('!active', a.dataset.pfTarget === name));
        });
      },
      { rootMargin: '-45% 0px -50% 0px', threshold: 0 }
    );
    sections.forEach((s) => this._spyObserver.observe(s));
  }

  /* ---------------- Sticky add-to-cart visibility ---------------- */

  _initStickyVisibility() {
    // Trigger off the buybox region — its bottom edge is the Add-to-cart button,
    // so the bar appears the moment the ATC scrolls off the top of the viewport
    // (not after the whole hero has passed). The region NODE is stable across
    // model/variant swaps (only its children are replaced), so observing it is
    // safe. Falls back to the overview wrapper if the buybox is missing.
    const overview = this.querySelector('[data-pf-anchor="overview"]');
    const trigger = this.querySelector('[data-pf-region="buybox"]') || overview;
    const sticky = this.querySelector('product-family-sticky');
    if (!trigger || !sticky) return;

    // In theme-preview sessions, Shopify's preview toolbar overlays the bottom
    // ~48px of the viewport; lift the bar above it (no effect on the live site).
    this._previewOffset = document.querySelector('iframe[src*="preview-bar"]') ? '48px' : '';

    // Show the sticky bar once the hero/buy area has scrolled above the top.
    // (Re-applied on every evaluation because model switches replace the bar node.)
    const setSticky = (scrolledPast) => {
      const bar = this.querySelector('product-family-sticky');
      if (!bar) return;
      if (this._previewOffset) bar.style.bottom = this._previewOffset;
      // Mobile sticky mode: this bar is the ONLY Add to cart, so it never hides.
      // The hidden ATTRIBUTE is removed rather than overridden in CSS for two
      // reasons: core.css declares [hidden]{display:none!important}, which no
      // author rule can outrank, and `hidden` also removes the bar from the
      // accessibility tree — which would leave screen-reader users with no way
      // to add to cart. [sticky-ready] then lets the CSS hide the buy box's own
      // button; setting it HERE means it is only ever set when the bar really
      // was revealed, so a broken script leaves the native button in place.
      if (this.hasAttribute('sticky-mobile') && this._stickyMQ && this._stickyMQ.matches) {
        bar.hidden = false;
        this.setAttribute('sticky-ready', '');
        // Only now does the bar have a measurable height to reserve.
        this._syncStickySpace();
        return;
      }
      this.removeAttribute('sticky-ready');
      bar.hidden = !scrolledPast;
    };
    this._evaluateSticky = () => setSticky(trigger.getBoundingClientRect().bottom < 0);

    this._stickyObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setSticky(!entry.isIntersecting && entry.boundingClientRect.top < 0);
        });
      },
      { threshold: 0 }
    );
    this._stickyObserver.observe(trigger);
  }
});

/* ---------------- Gallery numeric counter ---------------- */
customElements.define('product-family-gallery', class extends Core {
  render() {
    this._counter = this.querySelector('[data-element="gallery-counter-current"]');
    if (!this._counter) return;
    const evt = Events && Events.PRODUCT_GALLERY_SLIDE_CHANGE;
    if (!evt) return;
    this.sub(evt, (index) => {
      const n = (typeof index === 'number' ? index : (index && index.index) || 0) + 1;
      this._counter.textContent = String(n);
    });
  }
});

/* ---------------- "Learn more" description toggle ---------------- */
customElements.define('product-family-toggle', class extends Core {
  render() {
    const trigger = this.querySelector('[data-element="toggle-trigger"]');
    const body = this.querySelector('[data-element="toggle-body"]');
    if (!trigger || !body) return;
    trigger.addEventListener('click', () => {
      const collapsed = body.getAttribute('data-collapsed') !== 'false';
      body.setAttribute('data-collapsed', collapsed ? 'false' : 'true');
      trigger.setAttribute('aria-expanded', collapsed ? 'true' : 'false');
      const more = trigger.querySelector('[data-toggle-more]');
      const less = trigger.querySelector('[data-toggle-less]');
      if (more) more.hidden = collapsed;
      if (less) less.hidden = !collapsed;
    });
  }
});

/* ---------------- Sticky bar expand/collapse ---------------- */
customElements.define('product-family-sticky', class extends Core {
  render() {
    // DELEGATED, not bound to the button: the bar's head row is a swappable
    // region, so the toggle element is replaced on every model/variant change
    // while this element — and the drawer beside it — persist. A directly
    // bound listener would die on the first model switch.
    this._onClick = (e) => {
      const trigger = e.target.closest('[data-element="sticky-toggle"]');
      if (!trigger || !this.contains(trigger)) return;
      this._setOpen(!this.classList.contains('@open'));
    };
    this.addEventListener('click', this._onClick);
    this._setOpen(false);
  }

  // The open state lives as a class on THIS element (which survives swaps);
  // the drawer's hidden attribute and the chevron's aria-expanded follow it.
  _setOpen(on) {
    this.classList.toggle('@open', on);
    const panel = this.querySelector('[data-element="sticky-panel"]');
    if (panel) panel.hidden = !on;
    this.syncToggle();
    // The drawer shows the live variant options; the host owns moving them.
    const host = this.closest('product-family');
    if (host && typeof host._syncOptionsPlacement === 'function') host._syncOptionsPlacement();
  }

  // Re-apply the current state to the chevron. Called after a head-row swap,
  // where a freshly rendered toggle always arrives with aria-expanded="false".
  syncToggle() {
    const on = this.classList.contains('@open');
    this.querySelectorAll('[data-element="sticky-toggle"]').forEach((t) => {
      t.setAttribute('aria-expanded', on ? 'true' : 'false');
    });
  }

  collapse() { this._setOpen(false); }

  destroy() {
    if (this._onClick) this.removeEventListener('click', this._onClick);
  }
});